import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Mail, Plus, Trash2, Clock, PowerOff } from "lucide-react";
import { toast } from "sonner";

const REPORT_LABELS = {
  disposition_summary: "Disposition Summary",
  capture_rate: "Capture Rate (HQ / Sector / Emp Count)",
  productivity: "Agent Productivity",
  full_leads_csv: "Full Leads CSV",
};

const FREQ_LABELS = { hourly: "Hourly", daily: "Daily", weekly: "Weekly", monthly: "Monthly" };
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const BLANK = {
  name: "",
  report: "disposition_summary",
  frequency: "daily",
  hour: 9,
  weekday: 0,
  day_of_month: 1,
  recipients: "",
  active: true,
};

/** Tenant-scoped email scheduler manager.
 *  Runs against /api/lead-email-schedules; the actual delivery job is picked
 *  up by the existing APScheduler tick in server.py. */
export default function EmailSchedulerManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null | "new" | schedule object

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lead-email-schedules");
      setRows(data.schedules || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load schedules");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (row) => {
    try {
      await api.delete(`/lead-email-schedules/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/lead-email-schedules/${row.id}`, {
        ...row,
        recipients: row.recipients,
        active: !row.active,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="email-scheduler">
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-indigo-600" />
              <h3 className="font-heading text-sm font-semibold">Email Scheduler</h3>
            </div>
            <Button
              size="sm"
              onClick={() => setEditing("new")}
              className="h-8 bg-indigo-600 hover:bg-indigo-700"
              data-testid="scheduler-add-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New schedule
            </Button>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Recurring email deliveries of tenant reports. Each schedule runs against the currently-active client.
          </p>

          {loading ? (
            <div className="text-slate-400 py-6 text-xs text-center">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-slate-400 py-6 text-xs text-center border rounded" data-testid="scheduler-empty">
              No schedules yet. Add the first one above.
            </div>
          ) : (
            <div className="border rounded overflow-hidden divide-y divide-slate-100">
              {rows.map((r) => (
                <div key={r.id} className="p-3 flex items-center gap-3 text-xs" data-testid={`scheduler-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-800">{r.name}</span>
                      {!r.active && <Badge variant="outline" className="text-[9px]">Paused</Badge>}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {REPORT_LABELS[r.report] || r.report} · {FREQ_LABELS[r.frequency]}
                      {r.frequency === "weekly" && <> · {WEEKDAYS[r.weekday]}</>}
                      {r.frequency === "monthly" && <> · day {r.day_of_month}</>}
                      {r.frequency !== "hourly" && <> · {String(r.hour).padStart(2, "0")}:00</>}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5 font-mono truncate">
                      {(r.recipients || []).join(", ")}
                    </div>
                    {r.next_run_at && (
                      <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Next: {new Date(r.next_run_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost" size="icon" className="h-7 w-7 text-slate-500"
                      onClick={() => toggleActive(r)}
                      title={r.active ? "Pause" : "Resume"}
                      data-testid={`scheduler-toggle-${r.id}`}
                    >
                      <PowerOff className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon" className="h-7 w-7"
                      onClick={() => setEditing(r)}
                      data-testid={`scheduler-edit-${r.id}`}
                    >
                      <Mail className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon" className="h-7 w-7 text-rose-500"
                      onClick={() => del(r)}
                      data-testid={`scheduler-delete-${r.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {editing && (
        <ScheduleDialog
          initial={editing === "new" ? BLANK : {
            ...editing,
            recipients: (editing.recipients || []).join(", "),
          }}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ScheduleDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  const save = async () => {
    const recipients = String(form.recipients || "")
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!form.name.trim()) return toast.error("Name is required");
    if (recipients.length === 0) return toast.error("At least one recipient email is required");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        report: form.report,
        frequency: form.frequency,
        hour: Number(form.hour),
        weekday: form.frequency === "weekly" ? Number(form.weekday) : null,
        day_of_month: form.frequency === "monthly" ? Number(form.day_of_month) : null,
        recipients,
        active: !!form.active,
      };
      if (isEdit) {
        await api.patch(`/lead-email-schedules/${initial.id}`, payload);
        toast.success("Schedule updated");
      } else {
        await api.post("/lead-email-schedules", payload);
        toast.success("Schedule created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="scheduler-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit schedule" : "New schedule"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="h-8 text-xs mt-1"
              placeholder="e.g. Daily disposition digest"
              data-testid="scheduler-name-input"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Report</Label>
            <Select value={form.report} onValueChange={(v) => setForm((f) => ({ ...f, report: v }))}>
              <SelectTrigger className="h-8 text-xs mt-1" data-testid="scheduler-report-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(REPORT_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm((f) => ({ ...f, frequency: v }))}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="scheduler-freq-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(FREQ_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.frequency !== "hourly" && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">Hour (Asia/Qatar)</Label>
                <Input
                  type="number" min={0} max={23}
                  value={form.hour}
                  onChange={(e) => setForm((f) => ({ ...f, hour: Number(e.target.value) }))}
                  className="h-8 text-xs mt-1 font-mono"
                  data-testid="scheduler-hour-input"
                />
              </div>
            )}
            {form.frequency === "weekly" && (
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">Weekday</Label>
                <div className="flex gap-1 mt-1">
                  {WEEKDAYS.map((d, i) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, weekday: i }))}
                      className={`flex-1 h-8 text-xs rounded border ${
                        form.weekday === i
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-white text-slate-700 border-slate-200"
                      }`}
                      data-testid={`scheduler-weekday-${i}`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {form.frequency === "monthly" && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">Day of month (1–28)</Label>
                <Input
                  type="number" min={1} max={28}
                  value={form.day_of_month}
                  onChange={(e) => setForm((f) => ({ ...f, day_of_month: Number(e.target.value) }))}
                  className="h-8 text-xs mt-1 font-mono"
                  data-testid="scheduler-dom-input"
                />
              </div>
            )}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Recipients (comma-separated)</Label>
            <Input
              value={form.recipients}
              onChange={(e) => setForm((f) => ({ ...f, recipients: e.target.value }))}
              className="h-8 text-xs mt-1 font-mono"
              placeholder="ops@example.com, manager@example.com"
              data-testid="scheduler-recipients-input"
            />
          </div>
          <label className="flex items-center gap-2 text-xs pt-1">
            <Switch
              checked={!!form.active}
              onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
              data-testid="scheduler-active-toggle"
            />
            Active
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="scheduler-cancel">Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="scheduler-save"
          >
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Create schedule")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
